import React, {useState} from "react";
import {ClipBoardService} from "../../../../service/ClipBoardService";
import {useTranslation} from "react-i18next";
import {Collapse} from "react-bootstrap";
import {Link} from "react-router-dom";
import {StringService} from "../../../../services/StringService";
import {TermsAndConditions} from "../../../TermsAndConditions";

export const DMARCGeneratorForm = ({handleSendInfo}) => {
    const generateDmarcDnsEntry = (domain) => {
        // Start with the DMARC version and policy
        let dmarcEntry = `v=${domain.version}; p=${domain.policy}`;

        // Add optional fields if provided
        if (Array.isArray(domain.rua) && domain.rua.length > 0) {
            dmarcEntry += `; rua=${domain.rua.map(email => `mailto:${email}`).join(",")}`;
        }
        if (Array.isArray(domain.ruf) && domain.ruf.length > 0) {
            dmarcEntry += `; ruf=${domain.ruf.map(email => `mailto:${email}`).join(",")}`;
        }

        if (domain.adkim) {
            dmarcEntry += `; adkim=${domain.adkim}`;
        }

        if (domain.aspf) {
            dmarcEntry += `; aspf=${domain.aspf}`;
        }

        if (domain.pct === 0) {
            dmarcEntry += `; pct=${domain.pct}`;
        }

        if (domain.fo) {
            dmarcEntry += `; fo=${domain.fo}`;
        }

        if (domain.subdomainPolicy !== "none") {
            dmarcEntry += `; sp=${domain.subdomainPolicy}`;
        }

        if (domain.reportFormat) {
            dmarcEntry += `; rf=${domain.reportFormat}`;
        }

        if (domain.reportInterval !== 0) {
            dmarcEntry += `; ri=${domain.reportInterval}`;
        }

        return dmarcEntry;
    }

    const [acceptedTerms, setAcceptedTerms] = useState(false);

    const [domain, setDomain] = useState(
        {
            name: '',
            policy: 'quarantine',
            version: "DMARC1",
            spfIncludes: '',
            dkimSelector: '',
            rua: [],
            ruf: [],
            adkim: '',
            aspf: '',
            pct: 0,
            failureOption: "",
            subdomainPolicy: "",
            reportFormat: "",
            reportInterval: 3600
        }
    );

    const policies = [
        "none",
        "quarantine",
        "reject",
    ];
    const adkims = [
        {
            name: 'strict',
            value: 's'
        },
        {
            name: 'relax',
            value: 'r'
        }
    ];
    const aspfs = [
        {
            name: 'strict',
            value: 's'
        },
        {
            name: 'relax',
            value: 'r'
        }
    ];
    const failureOptions = [
        {
            name: 'Both',
            value: "0"
        },
        {
            name: 'SPF or DKIM',
            value: "1"
        },
        {
            name: 'DKIM',
            value: 'd'
        },
        {
            name: 'SPF',
            value: 's'
        }
    ];
    const [tempRua, setTempRua] = useState('');
    const [tempRuf, setTempRuf] = useState('');
    const [dnsEntry, setDnsEntry] = useState("");
    const [email, setEmail] = useState('');

    const handleChange = (e) => {
        const {name, value, type} = e.target;

        setDomain((prevForm) => {
            if (Array.isArray(prevForm[name])) {
                // If it's an array (e.g., rua/ruf), append values
                return {
                    ...prevForm,
                    [name]: [...prevForm[name], value], // ✅ Append to array
                };
            }

            return {
                ...prevForm,
                [name]: type === "radio" ? value : value
            };
        });
    };

    const handleAddRua = () => {
        if (!tempRua.trim() || domain.rua.includes(tempRua)) return;

        setDomain((prevForm) => ({
            ...prevForm,
            rua: [...prevForm.rua, tempRua],
        }));
        setTempRua('');
    };

    const handleAddRuf = () => {
        if (!tempRuf.trim() || domain.ruf.includes(tempRuf)) return;

        setDomain((prevForm) => ({
            ...prevForm,
            ruf: [...prevForm.ruf, tempRuf],
        }));
        setTempRuf('');
    };

    const handleRemoveRua = (value) => {
        setDomain((prevForm) => ({
            ...prevForm,
            rua: prevForm.rua.filter((item) => item !== value),
        }));
    };

    const handleRemoveRuf = (value) => {
        setDomain((prevForm) => ({
            ...prevForm,
            ruf: prevForm.ruf.filter((item) => item !== value),
        }));
    };

    const clipBoardService = new ClipBoardService();
    const stringService = new StringService();

    const [error, setError] = useState('');
    const handleAddDomain = (e) => {
        e.preventDefault();
        setError("");
        if (!domain.name.trim() || !stringService.isValidUrl(domain.name.trim())) {
            setError(t("pages.dmarcGenerator.generator.form.error.noDomainGiven"));
            return;
        }
        setDnsEntry(generateDmarcDnsEntry(domain));
    };

    const {t} = useTranslation();
    const [open, setOpen] = useState(false);

    return (
        <div className={"container-fluid"}>
            <form onSubmit={handleAddDomain}
                  className="mb-4 bg-white p-4 p-md-5 rounded rounded-3 shadow d-flex flex-column">

                <div className={"container-fluid m-0 p-0 mb-5"}>
                    <div className={"row"}>
                        <div className={"col"}>
                            {/*domain*/}
                            <div style={{marginBottom: "20px"}}>
                                <div className={"d-flex"}>
                                    <label
                                        className="form-label">{t("pages.dmarcGenerator.generator.form.domainName.title")}</label>
                                    <i
                                        className="bi bi-info-circle ms-2"
                                        data-bs-toggle="tooltip"
                                        data-bs-placement="right"
                                        title={t("pages.dmarcGenerator.generator.form.domainName.info")}
                                        style={{cursor: "pointer"}}
                                    ></i>
                                </div>


                                <input
                                    type="text"
                                    id="domainName"
                                    placeholder={t("pages.dmarcGenerator.generator.form.domainName.placeholder")}
                                    name="name"
                                    value={domain.name}
                                    onChange={handleChange}
                                    className="form-control"
                                />
                            </div>
                        </div>
                    </div>

                    <div className={"row"}>
                        <div className={"col"}>
                            {/*version*/}
                            <div className="mb-3">
                                <label
                                    className="form-label d-flex align-items-center">{t("pages.dmarcGenerator.generator.form.version.title")}
                                    <i
                                        className="bi bi-info-circle ms-2"
                                        data-bs-toggle="tooltip"
                                        data-bs-placement="right"
                                        title={t("pages.dmarcGenerator.generator.form.version.info")}
                                        style={{cursor: "pointer"}}
                                    ></i>
                                </label>
                                <select
                                    name={"version"}
                                    value={domain.version}
                                    onChange={handleChange}
                                    className="form-select"

                                >
                                    <option value="DMARC1">DMARC1</option>
                                </select>

                            </div>
                        </div>
                    </div>
                    <div className={"row"}>
                        <div className={"col"}>
                            {/*policy*/}
                            <div className="mb-3">
                                <label className="form-label d-flex align-items-center">
                                    {t("pages.dmarcGenerator.generator.form.policy.title")} <i
                                    className="bi bi-info-circle ms-2"
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="right"
                                    title={t("pages.dmarcGenerator.generator.form.policy.info")}
                                    style={{cursor: "pointer"}}
                                ></i>
                                </label>
                                <div className="d-flex flex-wrap gap-3">
                                    {policies.map((policy) => (
                                        <div className="form-check form-check-inline" key={policy}>
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="policy"
                                                id={`policy-${policy}`}
                                                value={policy}
                                                checked={domain.policy === policy}
                                                onChange={handleChange}
                                            />
                                            <label className="form-check-label" htmlFor={`policy-${policy}`}>
                                                {policy}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={"row"}>
                        <div className={"col justify-content-center d-flex"}>
                            <Link
                                onClick={() => setOpen(!open)}
                                aria-controls="collapse-content"
                                aria-expanded={open}
                                variant="outline-primary"
                                size="sm"
                            >
                                {t("pages.dmarcGenerator.generator.form.showMore")}
                            </Link>
                        </div>
                    </div>
                </div>

                <div className={"container-fluid p-0 m-0"}>
                    <Collapse in={open}
                              onEnter={() => {
                                  setDomain(prev => ({
                                      ...prev,
                                      adkim: 's',
                                      aspf: 's',
                                      percentage: 100,
                                      failureOption: "1",
                                      subdomainPolicy: "quarantine",
                                      reportFormat: "afrf",
                                      reportInterval: 86400
                                  }));
                              }}
                              onExit={
                                  () => {
                                      setDomain(prev => ({
                                          ...prev,
                                          adkim: '',
                                          aspf: '',
                                          percentage: 0,
                                          failureOption: "",
                                          subdomainPolicy: "",
                                          reportFormat: "",
                                          reportInterval: 3600
                                      }));
                                  }
                              }

                    >
                        <div id={"collapse-content"}>
                            <div className="mb-3">
                                <label className="form-label d-flex align-items-center">
                                    {t("pages.dmarcGenerator.generator.form.adkim.title")}
                                    <i
                                        className="bi bi-info-circle ms-2"
                                        data-bs-toggle="tooltip"
                                        data-bs-placement="right"
                                        title={t("pages.dmarcGenerator.generator.form.adkim.info")}
                                        style={{cursor: 'pointer'}}
                                    ></i>
                                </label>

                                <div className="d-flex flex-wrap gap-3">
                                    {adkims.map((option) => (
                                        <div className="form-check form-check-inline" key={option.value}>
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="adkim"
                                                id={`adkim-${option.value}`}
                                                value={option.value}
                                                checked={domain.adkim === option.value}
                                                onChange={handleChange}
                                            />
                                            <label className="form-check-label" htmlFor={`adkim-${option.value}`}>
                                                {option.name}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="mb-3">
                                <label className="form-label d-flex align-items-center">
                                    {t("pages.dmarcGenerator.generator.form.aspf.title")}
                                    <i
                                        className="bi bi-info-circle ms-2"
                                        data-bs-toggle="tooltip"
                                        data-bs-placement="right"
                                        title={t("pages.dmarcGenerator.generator.form.aspf.info")}
                                        style={{cursor: 'pointer'}}
                                    ></i>
                                </label>

                                <div className="d-flex flex-wrap gap-3">
                                    {aspfs.map((option) => (
                                        <div className="form-check form-check-inline" key={option.value}>
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="aspf"
                                                id={`aspf-${option.value}`}
                                                value={option.value}
                                                checked={domain.aspf === option.value}
                                                onChange={handleChange}
                                            />
                                            <label className="form-check-label" htmlFor={`aspf-${option.value}`}>
                                                {option.name}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>


                            <div className={"rounded bg-info-subtle p-3 mt-3 mb-3 "}>
                                {t("pages.dmarcGenerator.generator.form.hint")}
                            </div>

                            <div className="mb-3">
                                <label htmlFor="subdomainPolicy" className="form-label d-flex align-items-center">
                                    {t("pages.dmarcGenerator.generator.form.subdomainPolicy.title")}
                                    <i
                                        className="bi bi-info-circle ms-2"
                                        data-bs-toggle="tooltip"
                                        data-bs-placement="right"
                                        title={t("pages.dmarcGenerator.generator.form.subdomainPolicy.info")}
                                        style={{cursor: 'pointer'}}
                                    ></i>
                                </label>

                                <select
                                    id="subdomainPolicy"
                                    className="form-select"
                                    name="subdomainPolicy"
                                    value={domain.subdomainPolicy}
                                    onChange={handleChange}
                                >
                                    <option value="none">None</option>
                                    <option value="quarantine">Quarantine</option>
                                    <option value="reject">Reject</option>
                                </select>
                            </div>

                            <div className="mb-3">
                                <label htmlFor="percentage" className="form-label d-flex align-items-center">
                                    {t("pages.dmarcGenerator.generator.form.percentage.title")}
                                    <i
                                        className="bi bi-info-circle ms-2"
                                        data-bs-toggle="tooltip"
                                        data-bs-placement="right"
                                        title={t("pages.dmarcGenerator.generator.form.percentage.info")}
                                        style={{cursor: 'pointer'}}
                                    ></i>
                                </label>

                                <input
                                    type="range"
                                    id="percentage"
                                    name="percentage"
                                    min="0"
                                    max="100"
                                    value={domain.pct}
                                    onChange={handleChange}
                                    className="form-range"
                                    disabled={domain.policy === "none"}
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="reportInterval" className="form-label d-flex align-items-center">
                                    {t("pages.dmarcGenerator.generator.form.reportInterval.title")}
                                    <i
                                        className="bi bi-info-circle ms-2"
                                        data-bs-toggle="tooltip"
                                        data-bs-placement="right"
                                        title={t("pages.dmarcGenerator.generator.form.reportInterval.info")}
                                        style={{cursor: 'pointer'}}
                                    ></i>
                                </label>

                                <input
                                    type="number"
                                    id="reportInterval"
                                    name="reportInterval"
                                    value={domain.reportInterval}
                                    onChange={handleChange}
                                    className="form-control"
                                    min="3600"
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label d-flex align-items-center">
                                    {t("pages.dmarcGenerator.generator.form.failureOptions.title")}
                                    <i
                                        className="bi bi-info-circle ms-2"
                                        data-bs-toggle="tooltip"
                                        data-bs-placement="right"
                                        title={t("pages.dmarcGenerator.generator.form.failureOptions.info")}
                                        style={{cursor: 'pointer'}}
                                    ></i>
                                </label>

                                <div className="d-flex flex-wrap gap-3">
                                    {failureOptions.map((option) => (
                                        <div className="form-check form-check-inline" key={option.value}>
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="failureOption"
                                                id={`failureOption-${option.value}`}
                                                value={option.value}
                                                checked={domain.failureOption === option.value}
                                                onChange={handleChange}
                                            />
                                            <label className="form-check-label"
                                                   htmlFor={`failureOption-${option.value}`}>
                                                {option.name}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>


                            <div className="mb-3">
                                <label htmlFor="reportFormat" className="form-label d-flex align-items-center">
                                    {t("pages.dmarcGenerator.generator.form.reportingFormat.title")}
                                    <i
                                        className="bi bi-info-circle ms-2"
                                        data-bs-toggle="tooltip"
                                        data-bs-placement="right"
                                        title={t("pages.dmarcGenerator.generator.form.reportingFormat.info")}
                                        style={{cursor: 'pointer'}}
                                    ></i>
                                </label>

                                <select
                                    id="reportFormat"
                                    name="reportFormat"
                                    value={domain.reportFormat}
                                    onChange={handleChange}
                                    className="form-select"
                                >
                                    <option value="afrf">AFRF</option>
                                    <option value="iodef">IODEF</option>
                                </select>
                            </div>

                            <div className="mb-3">
                                <label htmlFor="spfIncludes" className="form-label d-flex align-items-center">
                                    {t("pages.dmarcGenerator.generator.form.spfIncludes.title")}
                                    <i
                                        className="bi bi-info-circle ms-2"
                                        data-bs-toggle="tooltip"
                                        data-bs-placement="right"
                                        title={t("pages.dmarcGenerator.generator.form.spfIncludes.info")}
                                        style={{cursor: 'pointer'}}
                                    ></i>
                                </label>

                                <input
                                    type="text"
                                    id="spfIncludes"
                                    name="spfIncludes"
                                    placeholder={t("pages.dmarcGenerator.generator.form.spfIncludes.placeholder")}
                                    value={domain.spfIncludes}
                                    onChange={handleChange}
                                    className="form-control"
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="dkimSelector" className="form-label d-flex align-items-center">
                                    {t("pages.dmarcGenerator.generator.form.dkimSelector.title")}
                                    <i
                                        className="bi bi-info-circle ms-2"
                                        data-bs-toggle="tooltip"
                                        data-bs-placement="right"
                                        title={t("pages.dmarcGenerator.generator.form.dkimSelector.info")}
                                        style={{cursor: 'pointer'}}
                                    ></i>
                                </label>

                                <input
                                    type="text"
                                    id="dkimSelector"
                                    name="dkimSelector"
                                    placeholder={t("pages.dmarcGenerator.generator.form.dkimSelector.placeholder")}
                                    value={domain.dkimSelector}
                                    onChange={handleChange}
                                    className="form-control"
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label d-flex align-items-center">
                                    {t("pages.dmarcGenerator.generator.form.rua.title")}
                                    <i
                                        className="bi bi-info-circle ms-2"
                                        data-bs-toggle="tooltip"
                                        data-bs-placement="right"
                                        title={t("pages.dmarcGenerator.generator.form.dkimSelector.info")}
                                        style={{cursor: 'pointer'}}
                                    ></i>
                                </label>

                                <div className="input-group mb-2">
                                    <input
                                        type="email"
                                        className="form-control"
                                        placeholder={t("pages.dmarcGenerator.generator.form.rua.placeholder")}
                                        value={tempRua}
                                        onChange={(e) => setTempRua(e.target.value)}
                                    />
                                    <button type="button" onClick={handleAddRua}
                                            className="btn btn-secondary text-white">
                                        {t("pages.dmarcGenerator.generator.form.rua.add")}
                                    </button>
                                </div>

                                <div className="d-flex flex-wrap gap-2">
                                    {domain?.rua?.map((email, index) => (
                                        <span
                                            key={index}
                                            className="badge bg-secondary text-white"
                                            style={{cursor: 'pointer'}}
                                            onClick={() => handleRemoveRua(email)}
                                        >
                                {email} ✕
                            </span>
                                    ))}
                                </div>
                            </div>

                            <div className="mb-3">
                                <label className="form-label d-flex align-items-center">
                                    {t("pages.dmarcGenerator.generator.form.ruf.title")}
                                    <i
                                        className="bi bi-info-circle ms-2"
                                        data-bs-toggle="tooltip"
                                        data-bs-placement="right"
                                        title={t("pages.dmarcGenerator.generator.form.ruf.info")}
                                        style={{cursor: 'pointer'}}
                                    ></i>
                                </label>

                                <div className="input-group mb-2">
                                    <input
                                        type="email"
                                        className="form-control"
                                        placeholder={t("pages.dmarcGenerator.generator.form.ruf.placeholder")}
                                        value={tempRuf}
                                        onChange={(e) => setTempRuf(e.target.value)}
                                    />
                                    <button type="button" onClick={handleAddRuf}
                                            className="btn btn-secondary text-white">
                                        {t("pages.dmarcGenerator.generator.form.ruf.add")}
                                    </button>
                                </div>

                                <div className="d-flex flex-wrap gap-2">
                                    {domain?.ruf?.map((email, index) => (
                                        <span
                                            key={index}
                                            className="badge bg-secondary text-white"
                                            style={{cursor: 'pointer'}}
                                            onClick={() => handleRemoveRuf(email)}
                                        >
                                {email} ✕
                            </span>
                                    ))}
                                </div>
                            </div>

                        </div>
                    </Collapse>
                </div>
                <div className="mb-3 text-end">
                    <button type="submit" className="btn btn-secondary text-white">
                        {t("pages.dmarcGenerator.generator.form.generate")}
                    </button>
                </div>
                {dnsEntry && dnsEntry.length > 0 && (
                    <div className="alert alert-success mt-4 p-4 rounded-4 shadow-sm border-0">
                        <h4 className="mb-3">{t("pages.dmarcGenerator.generator.form.result.title")}</h4>
                        <p className="mb-4">{t("pages.dmarcGenerator.generator.form.result.subTitle")}</p>
                        <div
                            className="bg-light p-3 rounded d-flex justify-content-between align-items-center flex-wrap">
                            <code className="font-monospace text-break mb-2 mb-md-0 me-3">{dnsEntry}</code>
                            <button
                                type="button"
                                className="btn btn-info text-white"
                                onClick={() => clipBoardService.handleCopy(dnsEntry)}
                            >
                                {t("pages.dmarcGenerator.generator.form.result.copy")}
                            </button>
                        </div>

                        {/* Optional info form */}
                        <div className="mt-4 pt-3 border-top">
                            <h5 className="mb-2">{t("pages.dmarcGenerator.generator.form.result.emailForm.title")}</h5>
                            <p className="text-muted mb-3">{t("pages.dmarcGenerator.generator.form.result.emailForm.subtitle")}</p>
                            <div className="row g-2">
                                <div className="col-12 col-md-9">
                                    <input
                                        type="email"
                                        className="form-control"
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder={t("pages.dmarcGenerator.generator.form.result.emailForm.placeholder")}
                                        required
                                    />
                                </div>
                                <div className="col-12 col-md-3">
                                    <button type="submit" className="btn btn-primary w-100 text-white"
                                            disabled={!acceptedTerms}
                                            onClick={
                                                (e) => {
                                                    handleSendInfo(e, dnsEntry, email, domain)
                                                }
                                            }
                                    >
                                        {t("pages.dmarcGenerator.generator.form.result.emailForm.cta")}
                                    </button>
                                </div>
                            </div>
                            <p className="form-text mt-2">
                                {t("pages.dmarcGenerator.generator.form.result.emailForm.disclaimer")}
                            </p>
                            <TermsAndConditions checked={acceptedTerms}
                                                onChange={(e) => setAcceptedTerms(e.target.checked)}></TermsAndConditions>
                        </div>
                    </div>
                )}
                {error && (
                    <div className="alert alert-danger mt-4 p-4 rounded-4 shadow-sm border-0">
                        <h4 className="mb-3">{t("pages.dmarcGenerator.generator.form.error.title")}</h4>
                        <p className="mb-4">{error}</p>
                    </div>
                )}

            </form>
        </div>
    )
}